import { useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import type { CoverageGap } from "@shared/types/corpus/api.ts";
import { FormDialog } from "../../../../components/primitives/feedback/FormDialog/FormDialog";
import { useFormDialog } from "../../../../components/primitives/feedback/FormDialog/useFormDialog";

type Mode = "existing" | "new";

export interface ChooseCoverageGapDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** The gaps to choose from. */
  gaps: CoverageGap[];
  /** A gap left out of the choice: the one a claim is moved from. */
  excludeGapId?: string | null;
  /** Called with the chosen gap. Throw to surface an error and keep the dialog open. */
  onChoose: (gapId: string) => Promise<void>;
  /** Called with the name and note of a new gap, which is created and chosen. Throw likewise. */
  onCreate: (name: string, note: string) => Promise<void>;
}

/** Chooses the coverage gap claims go to: one that exists, or a new one made on the spot. */
export function ChooseCoverageGapDialog({
  open,
  onClose,
  title,
  gaps,
  excludeGapId = null,
  onChoose,
  onCreate,
}: ChooseCoverageGapDialogProps) {
  const options = gaps.filter((gap) => gap.id !== excludeGapId);
  const [mode, setMode] = useState<Mode>(options.length > 0 ? "existing" : "new");
  const [chosen, setChosen] = useState<CoverageGap | null>(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");

  const { dialogProps } = useFormDialog({
    onSubmit: async () => {
      if (mode === "existing") {
        if (!chosen) throw new Error("Choose a gap.");
        await onChoose(chosen.id);
      } else {
        await onCreate(name.trim(), note.trim());
      }
    },
    onClose: () => {
      setChosen(null);
      setName("");
      setNote("");
      onClose();
    },
  });

  return (
    <FormDialog
      open={open}
      title={title}
      submitLabel={mode === "existing" ? "Add" : "Create and add"}
      maxWidth="sm"
      submitDisabled={mode === "existing" ? chosen === null : name.trim().length === 0}
      {...dialogProps}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={mode}
        onChange={(_event, next: Mode | null) => next && setMode(next)}
        aria-label="Which gap"
      >
        <ToggleButton value="existing" disabled={options.length === 0}>
          Existing gap
        </ToggleButton>
        <ToggleButton value="new">New gap</ToggleButton>
      </ToggleButtonGroup>

      {mode === "existing" ? (
        <Autocomplete
          size="small"
          options={options}
          value={chosen}
          onChange={(_event, gap) => setChosen(gap)}
          getOptionLabel={(gap) => gap.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => <TextField {...params} label="Gap" />}
        />
      ) : (
        <>
          <TextField
            label="Name"
            size="small"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </>
      )}
    </FormDialog>
  );
}
